import React from 'react'
import { User, Briefcase, Settings, FileText, Building2, LucideIcon } from 'lucide-react'

/** The tabs of Profile & Settings. Kept in one place — it used to be repeated 3×. */
export const PROFILE_TAB_IDS = [
  'profile',
  'interests',
  'inquiries',
  'organization',
  'settings',
] as const

export type ProfileTabId = (typeof PROFILE_TAB_IDS)[number]

/** Narrow an untrusted `?tab=` value from the URL. */
export const isProfileTabId = (value: string | null): value is ProfileTabId =>
  !!value && (PROFILE_TAB_IDS as readonly string[]).includes(value)

interface Tab {
  id: ProfileTabId
  label: string
  icon: LucideIcon
}

interface ProfileTabsProps {
  activeTab: ProfileTabId
  onTabChange: (tabId: ProfileTabId) => void
  className?: string
}

const ProfileTabs: React.FC<ProfileTabsProps> = ({
  activeTab,
  onTabChange,
  className
}) => {
  const tabs: Tab[] = [
    { id: 'profile', label: 'Profile Information', icon: User },
    { id: 'interests', label: 'Investment Interest', icon: Briefcase },
    { id: 'inquiries', label: 'My Inquiries', icon: FileText },
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ]

  return (
    <div className={`overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${className || ''}`}>
      <div className="flex min-w-max">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id

          return (
            <div key={tab.id} className="px-[4px]">
              <button
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`relative flex items-center gap-1 px-[16px] pb-[10px] pt-[12px] font-plus-jakarta text-[12px] font-medium leading-4 whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-[#2d2923] dark:text-[#fdfdfd]'
                    : 'text-[rgba(45,41,35,0.8)] hover:text-[#2d2923] dark:text-[rgba(253,253,253,0.8)] dark:hover:text-[#fdfdfd]'
                }`}
              >
                <tab.icon size={12} />
                {tab.label}
                {isActive && (
                  <span className="absolute inset-x-0 bottom-0 h-[3px] rounded-tl-[4px] rounded-tr-[4px] bg-[#2d2923] dark:bg-[#fdfdfd]" />
                )}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ProfileTabs
